import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Trash2, Plus } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface Template {
  id: string;
  name: string;
  description: string | null;
  itemCount: number;
}

interface Item {
  id: string;
  templateId: string;
  title: string;
  description: string | null;
  defaultDueOffsetDays: number | null;
  sortOrder: number;
}

export function AdminSubtaskTemplatesPage(): React.ReactElement {
  useDocumentTitle('Subtask templates');
  const { agent } = useAuth();
  const toast = useToast();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [selected, setSelected] = useState<{ template: Template; items: Item[] } | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<{ templates: Template[] }>('/api/subtask-templates');
      setTemplates(r.templates);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load.');
    }
  }

  async function loadSelected(id: string) {
    try {
      const r = await apiFetch<{ template: Template; items: Item[] }>(`/api/subtask-templates/${id}`);
      setSelected(r);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to load template.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!agent || agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  async function createTemplate() {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await apiFetch('/api/subtask-templates', {
        method: 'POST',
        json: { name: newName.trim() },
      });
      setNewName('');
      await load();
      toast.success('Template created.');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Create failed.');
    } finally {
      setBusy(false);
    }
  }

  async function deleteTemplate(t: Template) {
    if (!confirm(`Delete "${t.name}"? Items inside will be removed too.`)) return;
    try {
      await apiFetch(`/api/subtask-templates/${t.id}`, { method: 'DELETE' });
      if (selected?.template.id === t.id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">Subtask templates</h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Reusable checklists you can apply to a project to seed its subtasks. Items can carry a
          default due-date offset (days from project start) so applying a template staggers
          deadlines.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Templates</h2>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              placeholder="New template name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void createTemplate()}
              className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
            />
            <Button onClick={() => void createTemplate()} disabled={busy || !newName.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {templates === null ? (
            <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
          ) : templates.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--color-muted)]">No templates yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-[var(--color-border)]">
              {templates.map((t) => (
                <li key={t.id} className="flex items-center justify-between py-2">
                  <button
                    type="button"
                    onClick={() => void loadSelected(t.id)}
                    className={
                      'flex-1 text-left text-sm hover:underline ' +
                      (selected?.template.id === t.id
                        ? 'font-semibold text-[var(--color-t3-blue)]'
                        : 'text-[var(--color-body)]')
                    }
                  >
                    {t.name}
                    <span className="ml-2 text-xs text-[var(--color-muted)]">{t.itemCount} items</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteTemplate(t)}
                    className="text-[var(--color-muted)] hover:text-[var(--color-danger)]"
                    title="Delete template"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {selected && <TemplateItemsCard selected={selected} onChanged={() => void loadSelected(selected.template.id)} />}
      </div>
    </div>
  );
}

function TemplateItemsCard({
  selected,
  onChanged,
}: {
  selected: { template: Template; items: Item[] };
  onChanged: () => Promise<void> | void;
}): React.ReactElement {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [offset, setOffset] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await apiFetch(`/api/subtask-templates/${selected.template.id}/items`, {
        method: 'POST',
        json: {
          title: title.trim(),
          defaultDueOffsetDays: offset === '' ? null : Number(offset),
        },
      });
      setTitle('');
      setOffset('');
      await onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Add failed.');
    } finally {
      setBusy(false);
    }
  }

  async function removeItem(item: Item) {
    if (!confirm(`Delete "${item.title}"?`)) return;
    try {
      await apiFetch(
        `/api/subtask-templates/${selected.template.id}/items/${item.id}`,
        { method: 'DELETE' },
      );
      await onChanged();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">{selected.template.name}</h2>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        {selected.items.length} items in this template.
      </p>

      {selected.items.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">No items yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--color-border)]">
          {selected.items.map((it) => (
            <li key={it.id} className="flex items-start justify-between gap-2 py-2 text-sm">
              <div className="flex-1">
                <div>{it.title}</div>
                {it.defaultDueOffsetDays !== null && (
                  <div className="text-xs text-[var(--color-muted)]">
                    Due {it.defaultDueOffsetDays >= 0 ? '+' : ''}{it.defaultDueOffsetDays} days from project start
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => void removeItem(it)}
                className="text-[var(--color-muted)] hover:text-[var(--color-danger)]"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_120px_auto]">
        <input
          type="text"
          placeholder="New item title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
        />
        <input
          type="number"
          placeholder="Due +N days"
          value={offset}
          onChange={(e) => setOffset(e.target.value)}
          className="rounded-[var(--radius-button)] border border-[var(--color-border)] px-3 py-2 text-sm"
        />
        <Button onClick={() => void add()} disabled={busy || !title.trim()}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}
